// App-session plain-data store for per-plan recurring generation outcomes.
//
// Every record is keyed by the validated launch identity — user,
// organization, then plan — so a recovery notice or an in-flight request can
// never surface under another account, another organization, or another plan.
// Nothing is persisted; a full page load (the way this app changes identity)
// clears it.
import { useSyncExternalStore } from "react";
import { useSession } from "@/lib/session";
import { useOrganization } from "@/lib/organization";

export interface PlanGenerateScope {
  organization_id: string;
  user_id: string;
}

interface HeldPlanGenerate {
  organizationId: string;
  userId: string;
  scheduleId: string;
  legalEntityId: string;
  occurrenceDate: string;
  kind: "invoice" | "bill";
  partyName: string;
}

interface PendingPlanGenerate {
  organizationId: string;
  userId: string;
  scheduleId: string;
  kind: "invoice" | "bill";
}

interface Identity {
  organizationId: string;
  userId: string;
}

const heldPlans = new Map<string, HeldPlanGenerate>();
const pendingPlans = new Map<string, PendingPlanGenerate>();
const listeners = new Set<() => void>();

function recordKey(identity: Identity, scheduleId: string): string {
  return [identity.organizationId, identity.userId, scheduleId].join("\0");
}

function scopeKey(scope: PlanGenerateScope): string {
  return [scope.organization_id, scope.user_id].join("\0");
}

function scopeIdentity(
  scope: Pick<PlanGenerateScope, "organization_id" | "user_id">,
): Identity {
  return {
    organizationId: scope.organization_id,
    userId: scope.user_id,
  };
}

function publish(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function markPlanGenerateHeld(held: HeldPlanGenerate): void {
  heldPlans.set(recordKey(held, held.scheduleId), held);
  publish();
}

export function clearPlanGenerateRecoveryFor(
  scope: Pick<PlanGenerateScope, "organization_id" | "user_id">,
  scheduleId: string,
): void {
  if (heldPlans.delete(recordKey(scopeIdentity(scope), scheduleId))) publish();
}

/** Clears every held and in-flight record. Used for test isolation and
 * identity resets; a full page load clears them too. */
export function resetPlanGenerateState(): void {
  if (heldPlans.size === 0 && pendingPlans.size === 0) return;
  heldPlans.clear();
  pendingPlans.clear();
  publish();
}

export function markPlanGeneratePending(pending: PendingPlanGenerate): void {
  pendingPlans.set(recordKey(pending, pending.scheduleId), pending);
  publish();
}

export function clearPlanGeneratePending(
  scope: Pick<PlanGenerateScope, "organization_id" | "user_id">,
  scheduleId: string,
): void {
  if (pendingPlans.delete(recordKey(scopeIdentity(scope), scheduleId))) {
    publish();
  }
}

function firstRecordForScope<T extends Identity>(
  rows: Map<string, T>,
  scope: PlanGenerateScope | null | undefined,
): T | null {
  if (!scope) return null;
  const prefix = `${scopeKey(scope)}\0`;
  for (const [key, row] of rows) {
    if (key.startsWith(prefix)) return row;
  }
  return null;
}

export function firstHeldPlanInScope(
  scope: PlanGenerateScope | null | undefined,
): HeldPlanGenerate | null {
  return firstRecordForScope(heldPlans, scope);
}

export function firstPendingPlanInScope(
  scope: PlanGenerateScope | null | undefined,
): PendingPlanGenerate | null {
  return firstRecordForScope(pendingPlans, scope);
}

export function useHeldPlanGenerate(
  scope: PlanGenerateScope | null | undefined,
  scheduleId: string | undefined,
): HeldPlanGenerate | undefined {
  return useSyncExternalStore(
    subscribe,
    () => (scope && scheduleId ? heldPlans.get(`${scopeKey(scope)}\0${scheduleId}`) : undefined),
    () => undefined,
  );
}

export function useAnyHeldPlanGenerate(
  scope: PlanGenerateScope | null | undefined,
): HeldPlanGenerate | null {
  return useSyncExternalStore(
    subscribe,
    () => firstHeldPlanInScope(scope),
    () => null,
  );
}

export function useAnyPendingPlanGenerate(
  scope: PlanGenerateScope | null | undefined,
): PendingPlanGenerate | null {
  return useSyncExternalStore(
    subscribe,
    () => firstPendingPlanInScope(scope),
    () => null,
  );
}

export function useAnyPlanGeneratePending(
  scope: PlanGenerateScope | null | undefined,
): boolean {
  return useSyncExternalStore(
    subscribe,
    () => firstPendingPlanInScope(scope) !== null,
    () => false,
  );
}

/** The validated launch identity for plan generation: the active Console
 * organization plus the signed-in user. Null until both are known. */
export function usePlanGenerateScope(): PlanGenerateScope | null {
  const { scope } = useOrganization();
  const { state } = useSession();
  if (!scope || state.status !== "authenticated") return null;
  return {
    organization_id: scope.organization_id,
    user_id: state.user.id,
  };
}
